import {AbstractControl, ValidationErrors, Validators} from "@angular/forms";
import {
    coverLetterBarcodeRegex,
    cprPattern,
    danishPhoneNumberPattern,
    danishPersonName,
    datePattern,
    decimalPattern,
    emptyOrOneDanishLetterNotXPattern,
    internationalPhoneNumberPattern,
    intPattern,
    textAndNumberOnlyPattern,
    textAndSpaceAndApostropheAmpersandNameCharactersOnlyPattern,
    textAndNumberAndSpaceAndApostropheAmpersandNameCharactersOnlyPattern,
    textAndSpaceOnlyPattern,
    timePattern,
    voterNumberRegex,
    voterSearchPattern,
} from "./patterns";
import {convertDateTime, isStringUndefinedOrEmpty} from "./helpers";

export class StringValidator {

  public static notBackInTime(control: AbstractControl): ValidationErrors | null {
    return convertDateTime(control.value) > new Date() ? null : {
      not_back_in_time: true
    }
  }

  public static textAreaValidation(control: AbstractControl): ValidationErrors | null {
    const regex = /^[\w\s'\/\-\\?@,\.()\+\!;:ÆØÅæøå]*$/;
    if (control.value && !regex.test(control.value)) {
      return { wrong_syntax: true };
    }
    return null;
  }

  public static endedWithQuestionMark(
    control: AbstractControl
  ): ValidationErrors | null {
    const isRequired = Validators.required(control);
    if (isRequired) {
      return isRequired;
    }
    return control.value.trim().slice(-1) === "?"
      ? null
      : {
          ended_with_question_mark: true,
        };
  }

  public static isValidReferendumTitle(
      control: AbstractControl
  ): ValidationErrors | null {
    return !control.value.trim().startsWith('Folkeafstemning om') || control.value.trim().length > 60
        ? { does_not_start_with_folkeafstemning_om: true }
        : null;
  }

  public static isValidCpr(control: AbstractControl): ValidationErrors | null {
    return control.value && !control.value?.match(cprPattern) ?
      {
        invalid_cpr: true,
      } : null;
  }
  public static isValidSingleCpr(control: AbstractControl): ValidationErrors | null {
    const cpr: string[] = control.value?.split(' ')
    const count = cpr.length - 1;
    if (count == 0) {
      return (cpr.filter(x => !x.match(cprPattern)).length > 0)
          ? {
            invalid_cpr_for_home_voter: true,
          } : null;
    }
    return null;
  }
  public static isValidVoterNumberOrCpr(control: AbstractControl): ValidationErrors | null {
    if (Array.isArray(control.value)) {
      const allValid = Array.from(control.value).every(e => e.match(cprPattern) || e.match(voterNumberRegex));
      return !allValid ? { invalid_voter_number_and_cpr_and_barcode: true } : null;
    }

    return control.value?.match(cprPattern) || control.value?.match(voterNumberRegex)
        ? null
        : {
            invalid_voter_number_and_cpr: true,
        };
  }
  public static isValidVoterNumberOrCprOrBarcode(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(cprPattern) || control.value?.match(voterNumberRegex) || control.value?.match(coverLetterBarcodeRegex)
        ? null
        : {
          invalid_voter_number_and_cpr_and_barcode: true,
        };
  }

  public static isValidCprNumbers(control: AbstractControl): ValidationErrors | null {
    const cpr: string[] = control.value?.split(' ')
    return (cpr.filter(x => !x.match(cprPattern)).length > 0)
        ? {
          invalid_multiple_cpr: true,
        } : null;
  }

  public static isValidEmail(control: AbstractControl): ValidationErrors | null {
    return (control.value.split('@').length != 2)
        ? {
          invalid_email: true,
        } : null;
  }

  public static isMaximumNumberOfMassRegistration(control: AbstractControl): ValidationErrors | null {
    const cpr: string[] = control.value?.trim().split(' ');
    return cpr.length > 50 ? {
      invalid_maximum_registrations: true
    } : null;
  }

  public static isMaximumNumberOfPrintCoverLetter(control: AbstractControl): ValidationErrors | null {
    const cpr: string[] = control.value?.trim().split(' ');
    return cpr.length > 200 ? {
      invalid_maximum_cover_letter_printing: true
    } : null;
  }

  public static textAndNumberOnly(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(textAndNumberOnlyPattern)
      ? null
      : {
        invalid_format: true,
      };
  }

  public static oneDanishLetterNotXAndMaxOneNumber(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(emptyOrOneDanishLetterNotXPattern)
      ? null
      : {
        invalid_format: true,
      };
  }

  public static personName(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(danishPersonName)
        ? null
        : {
          invalid_format: true,
        };
  }

  public static textAndSpaceOnly(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(textAndSpaceOnlyPattern)
      ? null
      : {
        invalid_format: true,
      };
  }

  public static textAndSpaceAndApostropheAmpersandNameCharactersOnly(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(textAndSpaceAndApostropheAmpersandNameCharactersOnlyPattern)
      ? null
      : {
        invalid_format: true
      }
  }

  public static textAndNumberAndSpaceAndApostropheAmpersandNameCharactersOnly(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(textAndNumberAndSpaceAndApostropheAmpersandNameCharactersOnlyPattern)
      ? null
      : {
        invalid_format: true
      }
  }

  public static textAndNumberAndSpaceAndApostropheAmpersandNameCharactersOrNullOnly(control: AbstractControl) {
    return (control.value == null || control.value === '')
        ? null
        : StringValidator.textAndNumberAndSpaceAndApostropheAmpersandNameCharactersOnly(control);
  }

  public static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if(!control.value){
      return null;
    }
    
    return (control.value || '').trim().length
        ? null
        : {
          invalid_format: true
        };
  }
  
  public static textAndSpaceOrNullOnly(control: AbstractControl): ValidationErrors | null {
    return control.value == null ? null : StringValidator.textAndSpaceOnly(control);
  }

  public static danishPostalCode (control: AbstractControl): ValidationErrors | null {
    const pattern = /^\d{4}$/;
    return   !control.value || pattern.test(control.value) ? null : {invalid_format: true};
  }

public static phoneNumber(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value;
  
    if (isStringUndefinedOrEmpty(value)) {
        return null;
    }
  
    return value.match(danishPhoneNumberPattern) || value.match(internationalPhoneNumberPattern)
        ? null 
        : {invalid_format: true};
}

  public static isTimeFormat(control: AbstractControl): ValidationErrors | null {
    return !control.value || control.value?.match(timePattern)
    ? null
    : { invalid_time_format: true }
  }
  public static isDateFormat(control: AbstractControl): ValidationErrors | null {
    return !control.value || control.value?.match(datePattern)
    ? null
    : { invalid_date_format: true }
  }

  public static isInt(control: AbstractControl): ValidationErrors | null {
    return control.value === '' || control.value?.match(intPattern)
    ? null
    : { invalid_integer: true }
  }

  public static isDecimal(control: AbstractControl): ValidationErrors | null {
    return control.value === '' || control.value?.match(decimalPattern)
    ? null
    : { invalid_decimal: true }
  }

  public static isJson(control: AbstractControl): ValidationErrors | null {
    if (control.value === '') {
      return null
    }
    try {
        JSON.parse(control.value);
    } catch (e) {
      return { invalid_json: true }
    }
    return null;
  }

  public static isTime(control: AbstractControl): ValidationErrors | null {
    return !control.value ||  control.value?.match(timePattern)
    ? null
    : { invalid_time: true }
  }
  
  public static isGreaterThanZero(control: AbstractControl): ValidationErrors | null {
    return control.value > 0 ? null : { not_greater_than_zero: true }
  }

  public static isNumberPositiveOrNoValue(control: AbstractControl): ValidationErrors | null {
    if (Number.isNaN(parseInt(control.value)) && control.value !== "" && control.value !== null) {
      return {must_be_number: true}
    }
    if (control.value % 1 !== 0) {
      return { must_be_whole_number: true };
    }
    return (control.value >= 0 || !control.value) ? null : { must_be_positive_number: true }
  }

  public static isNumberOrNoValue(control: AbstractControl): ValidationErrors | null {
    if (Number.isNaN(parseInt(control.value)) && control.value !== "" && control.value !== null) {
      return {must_be_number: true}
    }
    if (control.value % 1 !== 0) {
      return { must_be_whole_number: true };
    }
    return null;
  }
  
  public static isOddNumber(control: AbstractControl): ValidationErrors | null {
    return control.value % 2 !== 0 ? null : { even_number: true }
  }
  
  public static isWholeNumber(control: AbstractControl): ValidationErrors | null {
    return control.value % 1 === 0 ? null : { decimal_number: true }
  }
  
  public static isVoterSearchFormat(control: AbstractControl): ValidationErrors | null {
    return control.value?.match(voterSearchPattern)
      ? null
      : { invalid_voter_search_format: true }
  }
}
